import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { JOB_QUEUE_BATCH_COMPLETED_EVENT, JOB_QUEUE_CHANGED_EVENT, jobQueueTracker, TrackedBatch } from '../helpers/jobQueueTracker'
import { GetJobQueueBatchStatusResponse, JobQueueBatchStatus } from '../models/response/jobQueue/JobQueueResponses'
import { JobQueueService } from '../services/JobQueueService'

/**
 * Sağ altta duran ilerleme kutusu: kuyruğa alınan toplu işlerin "3/10 tamamlandı" durumu.
 * Sayfa değişse de açık kalır; iş bitince özet gösterir, hatalı kayıtlar İş Kuyruğu ekranında açılır.
 */
const POLL_MS = 2500
const AUTO_CLOSE_MS = 15000

const JobQueueProgress = () => {
    const navigate = useNavigate()
    const [tracked, setTracked] = useState<TrackedBatch[]>(jobQueueTracker.list())
    const [statuses, setStatuses] = useState<Record<string, JobQueueBatchStatus>>({})
    const completedRef = useRef<Record<string, boolean>>({})

    useEffect(() => {
        const onChange = () => setTracked(jobQueueTracker.list())
        window.addEventListener(JOB_QUEUE_CHANGED_EVENT, onChange)
        window.addEventListener('storage', onChange)
        return () => {
            window.removeEventListener(JOB_QUEUE_CHANGED_EVENT, onChange)
            window.removeEventListener('storage', onChange)
        }
    }, [])

    useEffect(() => {
        if (tracked.length == 0)
            return

        let stopped = false
        let timer: any

        const poll = async () => {
            try {
                const response: GetJobQueueBatchStatusResponse = await JobQueueService.getBatchStatus(tracked.map(x => x.batchId))

                if (stopped || !response.isSuccess)
                    return

                const map: Record<string, JobQueueBatchStatus> = {}
                response.data.forEach(x => map[x.batchId] = x)
                setStatuses(map)

                response.data.forEach(x => {
                    if (x.isCompleted && !completedRef.current[x.batchId]) {
                        completedRef.current[x.batchId] = true
                        window.dispatchEvent(new CustomEvent(JOB_QUEUE_BATCH_COMPLETED_EVENT, { detail: x }))

                        // Tamamı başarılıysa kutu kendiliğinden kapanır; hata varsa kullanıcı kapatana dek kalır.
                        if (x.failed == 0 && x.cancelled == 0)
                            setTimeout(() => jobQueueTracker.remove(x.batchId), AUTO_CLOSE_MS)
                    }
                })

                // Sunucuda bulunmayan (başka firmaya ait / silinmiş) takip bırakılır.
                tracked.filter(x => !map[x.batchId]).forEach(x => jobQueueTracker.remove(x.batchId))
            } catch {
                // Bağlantı anlık koparsa bir sonraki turda tekrar denenir.
            }
            finally {
                const allDone = tracked.every(x => completedRef.current[x.batchId])
                if (!stopped && !allDone)
                    timer = setTimeout(poll, POLL_MS)
            }
        }

        poll()

        return () => {
            stopped = true
            clearTimeout(timer)
        }
    }, [tracked])

    if (tracked.length == 0)
        return null

    return (
        <div className='ebbe-jobqueue-progress'>
            {
                tracked.map(t => {
                    const s = statuses[t.batchId]
                    const done = s ? s.succeeded + s.failed + s.cancelled : 0
                    const percent = s && s.total > 0 ? Math.round(done * 100 / s.total) : 0

                    return (
                        <div key={t.batchId} className='card shadow-sm mb-3' role='status' aria-live='polite'>
                            <div className='card-body py-4 px-5'>
                                <div className='d-flex align-items-center justify-content-between mb-2'>
                                    <span className='fw-bold text-gray-800'>{s?.jobTypeName ?? t.title}</span>
                                    {
                                        s?.isCompleted && (
                                            <button type='button' className='btn btn-sm btn-icon btn-active-light-primary' aria-label='Kapat' onClick={() => jobQueueTracker.remove(t.batchId)}>
                                                <i className='fa fa-times'></i>
                                            </button>
                                        )
                                    }
                                </div>

                                <div className='progress h-8px mb-2'>
                                    <div className={'progress-bar ' + (s && s.failed > 0 ? 'bg-warning' : 'bg-success')} role='progressbar' style={{ width: percent + '%' }} aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}></div>
                                </div>

                                <div className='d-flex align-items-center justify-content-between fs-7'>
                                    <span className='text-gray-700'>
                                        {
                                            !s ? 'Kuyruğa alındı…'
                                                : s.isCompleted ? `Bitti: ${s.succeeded}/${s.total} başarılı`
                                                    : `${done}/${s.total} tamamlandı` + (s.running > 0 ? ' · gönderiliyor' : '')
                                        }
                                        {s && s.failed > 0 && <span className='text-danger fw-bold'> · {s.failed} hatalı</span>}
                                        {s && s.cancelled > 0 && <span className='text-muted'> · {s.cancelled} iptal</span>}
                                    </span>
                                    <a className='fw-bold' href='#' onClick={(e) => { e.preventDefault(); navigate('/jobqueue/list?batchId=' + t.batchId) }}>Ayrıntı</a>
                                </div>
                            </div>
                        </div>
                    )
                })
            }
        </div>
    )
}

export default JobQueueProgress
